import DocumentToolbar from "../common/DocumentToolbar";
import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import ViewNotaModal from "../ViewNotaModal";
import {
  Container,
  Row,
  Col,
  Form,
  Table,
  Button,
  Alert,
  Spinner,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { authFetch } from "../utils/authFetch";
import { AsyncPaginate } from "react-select-async-paginate";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { parseISO, format, isValid as isValidDate } from "date-fns";
import SeriiModal from "./SeriiModal";

const fromISOToDate = (iso) => {
  try {
    return iso ? parseISO(iso) : null;
  } catch {
    return null;
  }
};

const toYMD = (d) => (d && isValidDate(d) ? format(d, "yyyy-MM-dd") : "");

const ymdToISODateTime = (ymd, hh = "00", mm = "00") =>
  ymd ? `${ymd}T${hh}:${mm}:00` : "";

export default function NirCreate() {
  const location = useLocation();
  const navigate = useNavigate();

  const qs = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const fromIntrare = qs.get("from_intrare");

  const [model, setModel] = useState(null);
  const [existingNir, setExistingNir] = useState(null);
  const [gestiuneId, setGestiuneId] = useState("");
  const [selGestiune, setSelGestiune] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showNota, setShowNota] = useState(false);
  const [seriiPicker, setSeriiPicker] = useState({ show: false, row: null });
  const [cuSerii, setCuSerii] = useState(false);

  useEffect(() => {
    let alive = true;
    const fetchFirma = async () => {
      try {
        const res = await authFetch("/firma/");
        const items = res?.items || res || [];
        if (items.length > 0 && alive) {
          setCuSerii(items[0].cu_serii === 1);
        }
      } catch {}
    };
    fetchFirma();
    return () => { alive = false; };
  }, []);


  const LIMIT = 20;

  const loadPaged = async (url, q, page, limit) => {
    const skip = (page - 1) * limit;
    const sep = url.includes("?") ? "&" : "?";
    const full = `${url}${sep}search=${encodeURIComponent(q)}&skip=${skip}&limit=${limit}`;
    const data = await authFetch(full);
    const items = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
        ? data.items
        : [];
    const total =
      typeof data?.total === "number"
        ? data.total
        : typeof data?.count === "number"
          ? data.count
          : undefined;
    const hasMore =
      typeof total === "number"
        ? skip + items.length < total
        : items.length === limit;
    return { items, hasMore };
  };

  const loadGestiuneOptions = async (inputValue, _l, additional) => {
    const page = additional?.page ?? 1;
    const q = (inputValue || "").trim();
    const bases = ["/gestiune/", "/gestiuni/", "/bxgestiune/"];
    for (const base of bases) {
      try {
        const { items, hasMore } = await loadPaged(base, q, page, LIMIT);
        const options = items.map((g) => ({
          value: g.id,
          label:
            `${g.bxcod ?? g.cod ?? g.code ?? g.id} — ${g.bxnume ?? g.denumire ?? g.bxdenumire ?? ""}`.trim(),
        }));
        return { options, hasMore, additional: { page: page + 1 } };
      } catch {}
    }
    return { options: [], hasMore: false, additional: { page } };
  };

  const handleGestiuneChange = (opt) => {
    setSelGestiune(opt);
    setGestiuneId(opt ? String(opt.value) : "");
  };

  const setBxdocDate = (d) => {
    if (model) {
      const ymd = toYMD(d);
      setModel((m) => ({
        ...m,
        bxdoc_date: ymd ? ymdToISODateTime(ymd, "00", "00") : "",
      }));
    }
  };

  useEffect(() => {
    console.log("[NirCreate] mounted, from_intrare =", fromIntrare);
  }, [fromIntrare]);

  useEffect(() => {
    if (!fromIntrare) {
      console.warn("[NirCreate] lipsa from_intrare in query string");
      return;
    }
    setLoading(true);
    setError("");
    const url = `/nir/prefill?intrare_id=${fromIntrare}`;
    console.log("[NirCreate] FETCH →", url);
    (async () => {
      try {
        const existing = await authFetch(`/nir/by-intrare/${fromIntrare}`);
        setExistingNir(existing);
        const draft = await authFetch(url);
        console.log("[NirCreate] PREFILL OK:", draft);
        setModel(draft);
        if (draft && draft.bxgestiune_id) {
          setGestiuneId(String(draft.bxgestiune_id));
          setSelGestiune({
            value: draft.bxgestiune_id,
            label: draft.gestiune_name || `Gestiune #${draft.bxgestiune_id}`,
          });
        }
      } catch (e) {
        console.error("[NirCreate] PREFILL ERR:", e);
        setError(String(e.message || e));
      } finally {
        setLoading(false);
      }
    })();
  }, [fromIntrare]);

  const save = async () => {
    if (!model) return;
    if (!gestiuneId) {
      alert("Selecteaza/completeaza Gestiunea.");
      return;
    }
    setSaving(true);
    setError("");
    // const payload = { ...model, bxgestiune_id: Number(gestiuneId) };
    const payload = {
      ...model,
      bxgestiune_id: Number(gestiuneId),
      bxvalftva: Number(model.bxvalftva ?? 0),
      bxvaltva: Number(model.bxvaltva ?? 0),
      bxtotalwtva: Number(model.bxtotalwtva ?? 0),
      lines: (model.lines ?? model.linii ?? []).map((l) => ({
        bxpart_id: l.bxpart_id,
        conversion_kg_per_unit: l.conversion_kg_per_unit ?? null,
        qty: Number(l.qty),
        price: Number(l.price),
        bxstocin_id: l.bxstocin_id ? Number(l.bxstocin_id) : null,
        intrare_line_id: l.intrare_line_id ? Number(l.intrare_line_id) : null,
        observatii: l.observatii || "",
      })),
    };
    console.log("[NirCreate] SAVE payload → /nir/", payload);
    try {
      const res = await authFetch("/nir/", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      console.log("[NirCreate] SAVE OK:", res);
      toast.success("NIR-ul a fost creat cu succes!");
      if (res && res.id) navigate(`/nir/${res.id}/edit`);
      else throw new Error("Raspuns invalid la creare NIR");
    } catch (e) {
      console.error("[NirCreate] SAVE ERR:", e);
      setError(String(e.message || e));
      toast.error(`Eroare la salvare: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="py-5 text-center">
        <Spinner animation="border" />
      </div>
    );
  if (error)
    return (
      <Container className="py-3">
        <Alert variant="danger">{error}</Alert>
      
      <SeriiModal
        readOnly={false}
        show={seriiPicker.show}
        onHide={() => setSeriiPicker({ show: false, row: null })}
        line={seriiPicker.row !== null ? model.lines[seriiPicker.row] : null}
        onSave={(serii) => {
          if (seriiPicker.row !== null) {
            const lines = [...model.lines];
            lines[seriiPicker.row] = { ...lines[seriiPicker.row], serii };
            setModel((m) => ({ ...m, lines }));
          }
        }}
      />

    </Container>
    );
  if (!model)
    return (
      <Container className="py-3">
        <Alert variant="warning">
          Nu exista date de precompletare. Ai ajuns aici fara{" "}
          <code>?from_intrare=ID</code>.
        </Alert>
        <Button as={Link} to="/intrare" variant="secondary">
          Inapoi la Intrari
        </Button>
      
      <SeriiModal
        readOnly={false}
        show={seriiPicker.show}
        onHide={() => setSeriiPicker({ show: false, row: null })}
        line={seriiPicker.row !== null ? model.lines[seriiPicker.row] : null}
        onSave={(serii) => {
          if (seriiPicker.row !== null) {
            const lines = [...model.lines];
            lines[seriiPicker.row] = { ...lines[seriiPicker.row], serii };
            setModel((m) => ({ ...m, lines }));
          }
        }}
      />

    </Container>
    );

  return (
    <Container className="py-3">
      <DocumentToolbar
        title={`NIR — creare din intrare #${model.source_intrare_id}`}
        busy={saving}
        onBack={() => navigate(model.source_intrare_id ? `/intrare/${model.source_intrare_id}/edit` : "/nir")}
        backLabel={model.source_intrare_id ? "Înapoi la intrare" : "Înapoi"}
        onSave={save} saveDisabled={!gestiuneId}
        actions={[
          { label: "Tipărește", disabled: !existingNir?.id, onClick: () => window.open(`/nir/${existingNir.id}/print`, "_blank") },
          { label: "Vezi nota contabilă", disabled: !existingNir?.id, onClick: () => setShowNota(true) },
        ]}
      />

      <p className="text-muted">{existingNir?.id ? "Tipărirea și nota contabilă se referă la NIR-ul salvat. Salvați pentru a aplica modificările din intrare." : "Salvați NIR-ul pentru a putea tipări și consulta nota contabilă."}</p>
      <Row className="g-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Furnizor</Form.Label>
            <Form.Control
              value={model.customer_name || model.contact_id || ""}
              readOnly
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Numar doc</Form.Label>
            <Form.Control
              value={model.bxdoc_no || ""}
              onChange={(e) =>
                setModel((m) => ({ ...m, bxdoc_no: e.target.value }))
              }
              disabled={!!fromIntrare}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Data</Form.Label>
            <div>
              <DatePicker
                selected={fromISOToDate(model.bxdoc_date)}
                onChange={setBxdocDate}
                dateFormat="dd.MM.yyyy"
                isClearable
                placeholderText="zz.ll.aaaa"
                className="form-control"
                wrapperClassName="w-100"
                disabled={!!fromIntrare}
              />
            </div>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Gestiune</Form.Label>
            <AsyncPaginate
              value={selGestiune}
              loadOptions={loadGestiuneOptions}
              onChange={handleGestiuneChange}
              isClearable
              placeholder="Cauta gestiune..."
              debounceTimeout={300}
              cacheOptions
              defaultOptions
              openMenuOnFocus
              additional={{ page: 1 }}
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : null
              }
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </Form.Group>
        </Col>
      </Row>

      <Table striped bordered hover responsive size="sm" className="mt-3">
        <thead>
          <tr>
            <th>Articol ID</th>
            <th>Denumire</th>
            <th>UM</th>
            <th className="text-end">Cant.</th>
            <th className="text-end">Pret</th>
            <th>Observatii</th>
          </tr>
        </thead>
        <tbody>
          {model.lines.map((ln, idx) => (
            <tr key={idx}>
              <td>{ln.bxpart_id}</td>
              <td>{ln.part_name || "-"}</td>
              <td><strong>{ln.stock_um_label || ln.um_label || ln.um || ""}</strong>{ln.stock_um_label&&<><br/><small>Factură: {ln.um_label}</small><br/><small>1 {ln.stock_um_label} = {Number(ln.conversion_kg_per_unit)} KG</small></>}</td>
              <td className="text-end">
                <Form.Control
                  className="text-end"
                  value={ln.stock_qty ?? ln.qty}
                  onChange={(e) => {
                    const lines = [...model.lines];
                    lines[idx] = { ...lines[idx], qty: e.target.value };
                    setModel((m) => ({ ...m, lines }));
                  }}
                  disabled={!!fromIntrare}
                />
                {ln.stock_um_label&&<small>Facturat: {ln.qty} {ln.um_label}</small>}
              </td>
              <td className="text-end">
                <Form.Control
                  className="text-end"
                  value={ln.stock_price ?? ln.price}
                  onChange={(e) => {
                    const lines = [...model.lines];
                    lines[idx] = { ...lines[idx], price: e.target.value };
                    setModel((m) => ({ ...m, lines }));
                  }}
                  disabled={!!fromIntrare}
                />
                {ln.environmental_fee?.enabled && <small className="d-block text-info">{ln.environmental_fee.accounting_treatment==='producer_importer' ? 'Obligație AFM proprie, înregistrată manual; taxa nu este inclusă în costul afișat.' : 'Taxa de mediu este inclusă în costul afișat.'}</small>}
                {ln.stock_um_label&&<small>Facturat: {ln.price} RON/{ln.um_label}</small>}
              </td>
              <td>
                <Form.Control
                  value={ln.observatii || ""}
                  onChange={(e) => {
                    const lines = [...model.lines];
                    lines[idx] = { ...lines[idx], observatii: e.target.value };
                    setModel((m) => ({ ...m, lines }));
                  }}
                  placeholder="Observatii linie..."
                />
              </td>
              <td className="text-center">
                {cuSerii && (
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    style={{ whiteSpace: "nowrap" }} onClick={() => setSeriiPicker({show: true, row: idx})}
                  >
                    Serii ({ln.serii?.length || 0})
                  </Button>
                )}
              </td>

            </tr>
          ))}
        </tbody>
      </Table>

      <p className="text-muted mt-3">Sursa: Intrare #{model.source_intrare_id} ({model.source_intrare_no || "—"})</p>

      {existingNir?.id && (
        <ViewNotaModal
          recType={1}
          docId={Number(existingNir.id)}
          show={showNota}
          onHide={() => setShowNota(false)}
        />
      )}
    
      <SeriiModal
        readOnly={false}
        show={seriiPicker.show}
        onHide={() => setSeriiPicker({ show: false, row: null })}
        line={seriiPicker.row !== null ? model.lines[seriiPicker.row] : null}
        onSave={(serii) => {
          if (seriiPicker.row !== null) {
            const lines = [...model.lines];
            lines[seriiPicker.row] = { ...lines[seriiPicker.row], serii };
            setModel((m) => ({ ...m, lines }));
          }
        }}
      />

    </Container>
  );
}
